import { useEffect, useState, useSyncExternalStore } from 'react';
import { X, Trash2 } from '../../icons';
import {
  listTaskGroups, patchTaskGroup, deleteTaskGroup, type TaskGroupDto,
} from '../../api/task-extras';
import { layerAlert, layerConfirm } from '../../components/LayerPopup/LayerPopup';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './MyTasksBoard.css';
import './TaskGroupManage.css';

interface TaskGroupManageModalProps {
  onClose: () => void;
  /** 이름이 바뀌거나 그룹이 지워졌을 때 — 부모가 목록·필터를 다시 읽는다. */
  onChanged: () => void;
}

/**
 * TaskGroupManageModal (2026-09-03) — 내 그룹의 **이름 바꾸기 · 지우기**.
 *
 * 종전에는 그룹을 ⋯ 메뉴에서 **만들 수만** 있었다. `patchTaskGroup`·`deleteTaskGroup` 은
 * API 클라이언트에 있는데 화면에서 부르는 곳이 하나도 없어서, 한 번 만든 그룹은 오타가 나도
 * 고칠 수 없고 지울 수도 없었다. 그 두 함수의 화면 쪽 짝이 이 모달이다.
 *
 * 그룹을 지워도 **업무는 지워지지 않는다** — 서버는 배치(task_group_items)만 FK cascade 로
 * 걷어낸다(task-groups.routes.ts DELETE). 확인창에서 그 사실을 먼저 말한다: '그룹'과 '업무'가
 * 한 화면에 같이 있어서, 묶음을 지우면 안의 것도 사라진다고 읽기 쉽다.
 */
export function TaskGroupManageModal({ onClose, onChanged }: TaskGroupManageModalProps) {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [groups, setGroups] = useState<TaskGroupDto[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEscapeClose(onClose, busyId !== null);

  useEffect(() => {
    let alive = true;
    void listTaskGroups()
      .then((gs) => {
        if (!alive) return;
        setGroups(gs);
        setNames(Object.fromEntries(gs.map((g) => [g.id, g.name])));
      })
      .catch((err) => { if (alive) setError((err as Error).message ?? tProject('project.group.err_save')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  /** 이름 저장 — 바뀐 줄에서만 부른다. 목록을 다시 읽지 않고 그 줄만 갈아 끼운다. */
  async function rename(g: TaskGroupDto) {
    const next = (names[g.id] ?? '').trim();
    if (!next || next === g.name) return;
    setBusyId(g.id);
    setError(null);
    try {
      const updated = await patchTaskGroup(g.id, { name: next });
      setGroups((cur) => cur.map((x) => (x.id === g.id ? { ...x, ...updated } : x)));
      setNames((cur) => ({ ...cur, [g.id]: updated.name }));
      onChanged();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? tProject('project.group.err_save'));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(g: TaskGroupDto) {
    const ok = await layerConfirm(
      `${tProject('project.group.delete_confirm', { name: g.name })}\n`
      + `${tProject('project.group.delete_keeps_tasks', { n: g.taskIds.length })}`,
      { danger: true },
    );
    if (!ok) return;
    setBusyId(g.id);
    setError(null);
    try {
      await deleteTaskGroup(g.id);
      setGroups((cur) => cur.filter((x) => x.id !== g.id));
      onChanged();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      void layerAlert(msg ?? tProject('project.group.err_delete'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div
      className="pj-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && busyId === null) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tProject('project.group.manage_title')}
    >
      <div ref={trapRef} className="pj-modal mtg-modal">
        <header className="mtg-head">
          <h2>{tProject('project.group.manage_title')}</h2>
          <button type="button" className="mtg-close" onClick={onClose} disabled={busyId !== null}>
            <X size={14} aria-hidden />
            {tProject('project.btn.close')}
          </button>
        </header>

        {loading && <div className="tgm-empty">{tProject('project.group.manage_loading')}</div>}
        {!loading && groups.length === 0 && (
          <div className="tgm-empty">{tProject('project.group.manage_empty')}</div>
        )}

        {groups.length > 0 && (
          <div className="tgm-list">
            {groups.map((g) => {
              const dirty = (names[g.id] ?? '').trim() !== g.name && (names[g.id] ?? '').trim() !== '';
              return (
                <div className="tgm-row" key={g.id}>
                  <span className="tgm-dot" style={{ background: g.color }} aria-hidden />
                  <input
                    className="tgm-name"
                    value={names[g.id] ?? ''}
                    maxLength={60}
                    disabled={busyId !== null}
                    aria-label={tProject('project.group.rename_aria', { name: g.name })}
                    onChange={(e) => setNames((cur) => ({ ...cur, [g.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') void rename(g); }}
                  />
                  <span className="tgm-count">
                    {tProject('project.group.task_count', { n: g.taskIds.length })}
                  </span>
                  {dirty && (
                    <button
                      type="button"
                      className="tgm-btn tgm-btn--save"
                      disabled={busyId !== null}
                      onClick={() => void rename(g)}
                    >
                      {busyId === g.id ? tProject('project.btn.saving') : tProject('project.group.rename_submit')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="tgm-btn tgm-btn--danger"
                    disabled={busyId !== null}
                    aria-label={tProject('project.group.delete_aria', { name: g.name })}
                    data-tip={tProject('project.group.delete_aria', { name: g.name })}
                    onClick={() => void remove(g)}
                  >
                    <Trash2 size={13} aria-hidden />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {error && <div className="pj-modal-error" role="alert">{error}</div>}

        <footer className="mtg-foot">
          <button type="button" className="mtg-cancel" onClick={onClose} disabled={busyId !== null}>
            {tProject('project.btn.close')}
          </button>
        </footer>
      </div>
    </div>
  );
}
